import { Box, VStack, HStack, Text, Input, Button, Divider, IconButton, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Alert, AlertIcon, AlertDescription } from '@chakra-ui/react';
import { FiMail, FiLock, FiPhone } from 'react-icons/fi';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import { loginWithEmail, loginWithGoogle, resetPassword, initializeRecaptcha, sendPhoneVerification, verifyPhoneCode } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isOpen: isPhoneModalOpen, onOpen: onPhoneModalOpen, onClose: onPhoneModalClose } = useDisclosure();

  // Check for verification success message from email verification page
  useEffect(() => {
    if (location.state?.message) {
      toast({
        title: location.state.messageType === 'success' ? 'Success' : 'Info',
        description: location.state.message,
        status: location.state.messageType || 'success',
        duration: 5000,
        isClosable: true,
      });
      // Clear the state to prevent showing the message again
      window.history.replaceState({}, document.title);
    }
  }, [location.state, toast]);

  useEffect(() => {
    // Initialize reCAPTCHA
    initializeRecaptcha('recaptcha-container-login');
  }, []);

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      navigate('/');
    } catch (e: any) {
      toast({ title: 'Google login failed', description: e.message, status: 'error' });
    }
  };

  const handleEmailLogin = async () => {
    if (!email || !password) {
      toast({ 
        title: 'Missing credentials', 
        description: 'Please enter both email and password.', 
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    try {
      await loginWithEmail(email, password);
      navigate('/');
    } catch (e: any) {
      let errorMessage = 'Sign in failed. Please try again.';
      
      // Provide specific error messages based on Firebase error codes
      if (e.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address. Please check your email or sign up.';
      } else if (e.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again or reset your password.';
      } else if (e.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address. Please enter a valid email.';
      } else if (e.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled. Please contact support.';
      } else if (e.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (e.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (e.message) {
        errorMessage = e.message;
      }
      
      toast({ 
        title: 'Sign in failed', 
        description: errorMessage, 
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleReset = async () => {
    if (!email) {
      toast({ title: 'Enter your email to reset password', status: 'info' });
      return;
    }
    try {
      await resetPassword(email);
      toast({ title: 'Password reset email sent', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Reset failed', description: e.message, status: 'error' });
    }
  };

  const handlePhoneLogin = async () => {
    if (!phone) {
      toast({ title: 'Phone number required', status: 'error' });
      return;
    }

    try {
      setIsVerifyingPhone(true);
      const result = await sendPhoneVerification(phone);
      setConfirmationResult(result);
      onPhoneModalOpen();
      toast({ title: 'Verification code sent', description: 'Please check your phone for the verification code.', status: 'success' });
    } catch (e: any) {
      toast({ title: 'Phone login failed', description: e.message, status: 'error' });
    } finally {
      setIsVerifyingPhone(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!confirmationResult || !verificationCode) {
      toast({ title: 'Verification code required', status: 'error' });
      return;
    }

    try {
      setIsVerifyingPhone(true);
      await verifyPhoneCode(confirmationResult, verificationCode);
      toast({ title: 'Login successful!', status: 'success' });
      onPhoneModalClose();
      navigate('/');
    } catch (e: any) {
      toast({ title: 'Verification failed', description: e.message, status: 'error' });
    } finally {
      setIsVerifyingPhone(false);
    }
  };

  return (
    <Box maxW="400px" mx="auto" mt={20}>
      <VStack gap={6} align="stretch">
        <VStack gap={2} textAlign="center">
          <Text fontSize="3xl" fontWeight="bold" color="white">
            Welcome Back
          </Text>
          <Text color="gray.400">
            Sign in to your Vindie Stream account
          </Text>
        </VStack>

        {location.state?.message && (
          <Alert status={location.state.messageType || 'success'} borderRadius="md">
            <AlertIcon />
            <AlertDescription fontSize="sm">
              {location.state.message}
            </AlertDescription>
          </Alert>
        )}

        <VStack gap={4}>
          <Input placeholder="Email" type="email" bg="gray.700" border="none" color="white" _placeholder={{ color: 'gray.400' }} _focus={{ bg: 'gray.600' }} value={email} onChange={(e) => setEmail(e.target.value)} />
          
          <Input placeholder="Password" type={'password'} bg="gray.700" border="none" color="white" _placeholder={{ color: 'gray.400' }} _focus={{ bg: 'gray.600' }} value={password} onChange={(e) => setPassword(e.target.value)} />
          
          <Input placeholder="Phone Number (Alternative)" type="tel" bg="gray.700" border="none" color="white" _placeholder={{ color: 'gray.400' }} _focus={{ bg: 'gray.600' }} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </VStack>

        <Button colorScheme="purple" size="lg" onClick={handleEmailLogin}>Sign In</Button>

        <HStack justify="center" gap={2}>
          <Text color="gray.400">Don't have an account?</Text>
          <Text
            color="purple.400"
            cursor="pointer"
            _hover={{ color: 'purple.300' }}
            onClick={() => navigate('/signup')}
          >
            Sign up
          </Text>
        </HStack>

        <Divider />

        <VStack gap={3}>
          <Button variant="outline" colorScheme="white" w="100%" onClick={handleGoogleLogin}>
            <IconWrapper icon={FiMail} size={20} style={{ marginRight: '8px' }} />
            Continue with Google
          </Button>
          
          <Button 
            variant="outline" 
            colorScheme="green" 
            w="100%" 
            onClick={handlePhoneLogin}
            isLoading={isVerifyingPhone}
            loadingText="Sending Code"
          >
            <IconWrapper icon={FiPhone} size={20} style={{ marginRight: '8px' }} />
            Sign in with Phone
          </Button>
          
          <Button variant="link" colorScheme="purple" onClick={handleReset}>Forgot password?</Button>
        </VStack>

        {/* Phone Verification Modal */}
        <Modal isOpen={isPhoneModalOpen} onClose={onPhoneModalClose}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Verify Phone Number</ModalHeader>
            <ModalBody>
              <VStack gap={4}>
                <Text color="gray.300">Enter the verification code sent to {phone}</Text>
                <Input 
                  placeholder="Verification Code" 
                  bg="gray.700" 
                  border="none" 
                  color="white" 
                  _placeholder={{ color: 'gray.400' }} 
                  _focus={{ bg: 'gray.600' }} 
                  value={verificationCode} 
                  onChange={(e) => setVerificationCode(e.target.value)} 
                />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onPhoneModalClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="green" 
                onClick={handleVerifyPhoneCode}
                isLoading={isVerifyingPhone}
                loadingText="Verifying"
              >
                Verify
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* reCAPTCHA Container */}
        <div id="recaptcha-container-login"></div>
      </VStack>
    </Box>
  );
};

export default Login;
